<script lang="ts" setup>
  import GleanWebSDK from '@gleanwork/web-sdk'

  import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import type { Ref } from 'vue'

  import { storeToRefs } from 'pinia'

  import { useGleanChat } from '#ourahub-intra/composables/glean'

  import { useAppStore } from '#ourahub-intra/stores/application'

  import type { Service } from '#ourahub-intra/types/services'

  const props = defineProps<{
    services: Service[]
  }>()

  const appStore = useAppStore()

  const { activeService } = storeToRefs(appStore)

  const { chatOpen, toggleChat, agentId, gleanApiUrl } = useGleanChat()

  const chatContainer = ref<HTMLElement | null>(null)

  const currentAgentId = ref(agentId)

  const renderedAgentId: Ref<string | null> = ref(null)

  watch(
    activeService,
    (service) => {
      currentAgentId.value = props.services.find((s) => s.id === service)?.agent || agentId
    },
    { immediate: true },
  )

  watch(chatOpen, async (isOpen) => {
    if (isOpen) {
      if (currentAgentId.value !== renderedAgentId.value) {
        if (chatContainer.value) {
          chatContainer.value.textContent = ''
        }

        renderedAgentId.value = currentAgentId.value

        await nextTick()

        GleanWebSDK.renderChat(chatContainer.value!, {
          backend: gleanApiUrl + '/',
          ...(currentAgentId.value ? { applicationId: currentAgentId.value } : {}),
        })
      }
    }
  })

  function handleKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'g') {
      e.preventDefault()

      toggleChat()
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeydown)

    if (chatContainer.value) {
      chatContainer.value.textContent = ''
    }
  })
</script>

<template lang="html">
  <!-- Chat panel -->
  <div v-show="chatOpen" role="dialog" aria-label="Help chat" class="glean-chat-panel">
    <div ref="chatContainer" class="glean-chat-container" />
  </div>

  <!-- FAB -->
  <v-tooltip location="left" text="Ask Glean">
    <template #activator="{ props: tooltipProps }">
      <o-btn-primary
        v-bind="tooltipProps"
        icon
        size="large"
        elevation="2"
        color="primary"
        class="glean-help-button"
        :aria-label="chatOpen ? 'Close help chat' : 'Open help chat'"
        @click="toggleChat()"
      >
        <v-icon>{{ chatOpen ? 'mdi-close' : 'mdi-assistant' }}</v-icon>
      </o-btn-primary>
    </template>
  </v-tooltip>
</template>

<style lang="scss" scoped>
  .glean-help-button {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 1000;
  }

  .glean-chat-panel {
    display: flex;
    position: fixed;
    right: 24px;
    bottom: 100px;
    flex-direction: column;
    z-index: 1099;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    border-radius: 12px;
    background: white;
    width: 420px;
    height: 600px;
    overflow: hidden;
  }

  .glean-chat-container {
    flex: 1;
    overflow: auto;
  }

  @media (max-width: 768px) {
    .glean-chat-panel {
      width: calc(100vw - 48px);
      max-width: 420px;
    }
  }

  @media (max-width: 480px) {
    .glean-chat-panel {
      right: 8px;
      left: 8px;
      width: calc(100vw - 16px);
      max-width: none;
    }
  }
</style>
